import { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import {
  Box,
  Grid,
  LinearProgress,
  Paper,
  Switch,
  Tab,
  Tabs,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import useModuleMetadata from 'src/hooks/use-module-metadata.hook';
import useMatomoFunction from 'src/hooks/use-matomo-function.hook';
import Storage from 'src/utils/storage/storage';
import { titleData } from '../../simulation-summary/utils/constants';
import '../../simulation-summary/simulation-summary.scss';
import {
  emptySelectedCompareScenariosList,
  handleMetricToggleChange,
  handleScenarioManagerToComparison
} from '../store/scenario-comparison-slice';

import { getCurrencyToggledata } from '../../simulation-summary/store/simulation-summary-slice';
import MODULE_TO_PAGE_MAP from '../../home/types/simulation-module-to-page-map';
import MetricComparisonBarChartContainer from './metric-comparison-bar-chart-container';
import { useModulePreRequisites } from '../../common';
import PotentialRiskTableContainerComponent from '../../simulation-summary/components/potential-risk-table/potential-risk-table-container';
import MultiyearEstimateComponent from '../../simulation-summary/components/multiyear-estimate/multiyear-estimate';
import AsIsToBeGrid from '../../simulation-summary/components/as-is-to-be-v2/as-is-to-be-v2';
import Summary from '../../simulation-summary/components/summary/summary';
import MultiyearEstimateTableComponent from '../../simulation-summary/components/multiyear-estimate/multiyear-estimate-table';
import { MatomoEventTrackMapObject } from '../../constants/constant';
import { resetSelectedScenario } from '../../pricing-simulator/store/pricing-simulator-slice';
import { handleCurrencyToggleStateComparison } from '../store/scenario-comparison-slice';

export default function ScenarioComparison() {
  const appDispatch = useAppDispatch();
  const [isUnitSelected, setIsUnitSelected] = useState(false);
  const [selectedScenario, setSelectedScenario] = useState(0);
  const [selectedScenarioIndex, setSelectedScenarioIndex] = useState(-1);
  const [selectedSummaryTab, setSelectedSummaryTab] = useState('summary');
  const [toggleScenarioGroup, setToggleScenarioGroup] = useState([]);
  const {
    selectedCompareScenariosList,
    toggleData,
    isScenarioManagerToComparison,
    isTargetEnabled
  } = useAppSelector((state) => state.scenarioComparison);

  const segmentDetailsSrorage = Storage.getSegmentDetails();

  // fetching summary tab details (Available in segmentDetailsData value)
  const { segmentDetailsData } = useAppSelector((state) => state.landing);
  // getting summary dynamic access
  const summaryDynamiceAccess = segmentDetailsData.summary_dynamic_access
    ? segmentDetailsData.summary_dynamic_access
    : segmentDetailsSrorage.summary_dynamic_access;

  const currencyToggle = useAppSelector((state) => state.simulationSummary.currencyToggle);
  //const isTargetEnabled = useAppSelector((state)=> state.scenarioComparison.isTargetEnabled)

  const local_currency = currencyToggle?.find((item) => item.order === 1);
  const target_currency = currencyToggle?.find((item) => item.order === 2);
  const isSameCurrency = local_currency?.alias === target_currency?.alias || !target_currency;
  useEffect(() => {
    appDispatch(getCurrencyToggledata({
      sourceId,
      scenarioId
    }));
  }, []);

  const toggleCurrencySwitch = (e) => {
    appDispatch(handleCurrencyToggleStateComparison(e.target.checked));
  };

  // filtering the tabs as per the visibility
  const visibleSummaryDynamicAccess = summaryDynamiceAccess.filter(
    (item) => item.is_visible === true
  );

  const tabsData: { alias: string; key: string; disable: boolean }[] =
    visibleSummaryDynamicAccess;

  const { moduleId, sourceId, scenarioId } = useModuleMetadata({
    module: MODULE_TO_PAGE_MAP.SUMMARY
  });

  const toggleMetricSwitch = (e) => {
    setIsUnitSelected(e.target.checked);
    appDispatch(handleMetricToggleChange({ metricToggle: e.target.checked }));
  };

  const onScenarioToggleChange = (e) => {
    const selectedScenarioId = Number(e.target.value);
    setSelectedScenario(selectedScenarioId);
    const scenarioIndex = toggleScenarioGroup.findIndex(
      (item) => item.key === selectedScenarioId
    );
    if (scenarioIndex >= 0) setSelectedScenarioIndex(scenarioIndex);
  };

  const handleSummaryTabChange = (value: string) => {
    setSelectedSummaryTab(value);
  };

  const { callFunction } = useMatomoFunction();
  const getPageName = () => {
    const pageTitle = window.location.href.split('/').at(-1);
    return pageTitle;
  };

  // useEffect(() => {
  //   appDispatch(resetSelectedScenario());

  //   callFunction(
  //     MatomoEventTrackMapObject.track_page_view.event_key,
  //     getPageName()
  //   );
  //   // Cleanup function to reset selected scenarios when the component unmounts
  //   return () => {
  //  //isScenarioManagerToComparison is 'true' when navigation happens from Scenario manager -> scenario comparison
  //       if(!isScenarioManagerToComparison){
  //          appDispatch(emptySelectedCompareScenariosList());
  //       }
  //       if (isScenarioManagerToComparison){
  //        appDispatch(handleScenarioManagerToComparison({isScenarioManagerToComparison: false}));
  //       }
  //   };

  // }, [isScenarioManagerToComparison]);

  useEffect(() => {
    // resetting the selected scenarios on load
    appDispatch(resetSelectedScenario());

    // calling matomo
    callFunction(
      MatomoEventTrackMapObject.track_page_view.event_key,
      getPageName()
    );

    // emptying scenario list in case of isScenarioManagerToComparison is false
    if (!isScenarioManagerToComparison) {
      appDispatch(emptySelectedCompareScenariosList());
    }

    // in case of isScenarioManagerToComparison true, making it false
    if (isScenarioManagerToComparison) {
      appDispatch(
        handleScenarioManagerToComparison({
          isScenarioManagerToComparison: false
        })
      );
    }
  }, []);

  useEffect(() => {
    if (
      selectedCompareScenariosList &&
      selectedCompareScenariosList.length > 0
    ) {
      const scenarioToggle = [];
      selectedCompareScenariosList.forEach((item, index) => {
        scenarioToggle.push({
          alias: `Scenario ${index + 1}`,
          key: Number(item.scenario_id)
        });
      });
      setToggleScenarioGroup(scenarioToggle);
    }
  }, [selectedCompareScenariosList]);

  useEffect(() => {
    if (toggleScenarioGroup && toggleScenarioGroup.length > 0) {
      setSelectedScenario(Number(toggleScenarioGroup[0].key));
      setSelectedScenarioIndex(0);
    }
  }, [toggleScenarioGroup]);

  const { isFetchingPreRequisites } = useModulePreRequisites({
    module: MODULE_TO_PAGE_MAP.SUMMARY,
    onPreRequisitesFetched() {
      // summary api call on load
    }
  });

  const selectedScenarioHandler = (scenario) => {
    const scenarioArr = toggleScenarioGroup.filter(
      (item) => item.alias === scenario.alias
    );
    if (scenarioArr && scenarioArr.length > 0)
      setSelectedScenario(Number(scenarioArr[0].key));
  };

  return isFetchingPreRequisites ? (
    <div className='parent-loader-div'>
      <LinearProgress className='loader-div' />{' '}
    </div>
  ) : (
    <div style={{ maxHeight: '87vh', overflow: 'auto' }}>
      <div className='chart-container' style={{ margin: '10px' }}>
        <Paper
          className='summary-div'
          elevation={0}
          data-testid='bar-chart-container'
        >
          <div
            className='container-title title-margin'
            style={{
              display: 'flex',
              justifyContent: 'space-between'
            }}
          >
            <div>{titleData.metricComparison}</div>
            {selectedCompareScenariosList &&
              selectedCompareScenariosList.length > 0 &&
              (

                <div style={{ display: 'inline-flex', alignItems: 'center', transform: 'translateX(-9px)' }}>
                  {!isSameCurrency && (<div>
                    <span className={`currency-label ${!isTargetEnabled ? 'active' : 'non-active'}`} >
                      {local_currency?.alias}
                    </span>
                    <Switch
                      onChange={toggleCurrencySwitch}
                      checked={isTargetEnabled}
                      size="small"
                    />
                    <span className={`currency-label ${isTargetEnabled ? 'active' : 'non-active'}`}>
                      {target_currency?.alias}
                    </span>
                  </div>)}
                    {toggleData.length > 0 && (<div>
                    <span
                      className={`${!isUnitSelected ? 'active' : 'non-active'}`}
                    >
                      Units*
                    </span>
                    <Switch
                      onChange={(e) => toggleMetricSwitch(e)}
                      size='small'
                    />
                    <span
                      className={`${isUnitSelected ? 'active' : 'non-active'}`}
                    >
                      Tonnes*
                    </span>
                  </div>)}
                </div>
              )}
          </div>
          <MetricComparisonBarChartContainer
            scenarioId={scenarioId}
            selectedScenarioHandler={selectedScenarioHandler}
            scenarioIndex={selectedScenarioIndex}
            isUnitSelected={isUnitSelected}
          />
        </Paper>
      </div>
      {selectedCompareScenariosList &&
        selectedCompareScenariosList.length > 0 && (
          <>
            <div
              style={{ borderBottom: '1px solid #ACACAC', marginTop: '10px' }}
            />
            <div className='chart-container' style={{ margin: '10px' }}>
              <div className='container-title title-margin'>
                Select Scenarios to see in detail
              </div>
            </div>
            <div style={{ marginLeft: '10px' }}>
              <ToggleButtonGroup
                size='small'
                color='primary'
                value={selectedScenario}
                exclusive
                onChange={(_event) => {
                  onScenarioToggleChange(_event);
                }}
              >
                {toggleScenarioGroup.map(
                  (toggleInfo: { alias: string; key: string }) => (
                    <ToggleButton value={toggleInfo.key}>
                      {toggleInfo.alias}
                    </ToggleButton>
                  )
                )}
              </ToggleButtonGroup>
            </div>
            {selectedCompareScenariosList.map((scenario) => {
              if (Number(scenario.scenario_id) !== selectedScenario) {
                return null;
              }
              return (
                <>
                  <div style={{ marginLeft: '10px' }}>
                    <Box sx={{ width: '100%' }}>
                      <Box>
                        {tabsData ? (
                          <Tabs
                            sx={{ fontSize: '14px' }}
                            value={selectedSummaryTab}
                            onChange={(_e, value) =>
                              handleSummaryTabChange(value)
                            }
                            aria-label='basic tabs example'
                          >
                            {/* rendering tabs */}
                            {tabsData.map((tabItem) => (
                              <Tab label={tabItem.alias} value={tabItem.key} />
                            ))}
                          </Tabs>
                        ) : (
                          <p>Tab data not available</p>
                        )}
                      </Box>
                    </Box>
                  </div>
                  <Grid container p={2}>
                    <Grid item xs={12}>
                      {selectedSummaryTab === 'summary' ? (
                        <Summary
                          scenarioId={scenario.scenario_id}
                          moduleId={moduleId}
                          sourceId={sourceId}
                          fetchDataWithEmptyFilters
                          mode='comparison'
                        />
                      ) : null}
                      {selectedSummaryTab === 'potentialRisks' ? (
                        <PotentialRiskTableContainerComponent
                          sourceId={sourceId}
                          moduleId={moduleId}
                          scenarioId={scenario.scenario_id}
                          disableInputs
                          mode='comparison'
                        />
                      ) : null}
                      {selectedSummaryTab === 'multiyearEstimation' ? (
                        <MultiyearEstimateComponent
                          sourceId={sourceId}
                          moduleId={moduleId}
                          scenarioId={scenario.scenario_id}
                          inputsDisabled
                          mode='comparison'
                        />
                      ) : null}
                      {selectedSummaryTab === 'asIsVsToBe' ? (
                        <AsIsToBeGrid
                          scenarioName={scenario.scenario_name}
                          sourceId={sourceId}
                          moduleId={moduleId}
                          scenarioId={scenario.scenario_id}
                          mode='comparison'

                        />
                      ) : null}
                    </Grid>
                  </Grid>
                </>
              );
            })}
          </>
        )}
    </div>
  );
}
